import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef
} from '@tanstack/react-table';
import { Pencil, Plus, Trash2, Upload, Users, Receipt, UserPlus } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { toast } from 'sonner';
import { get, post, patch, del, getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import {
  AdvancedListToolbar,
  useUrlSearchState,
  type FilterChip
} from '@/components/ui/advanced-list-toolbar';
import { CursorPaginator } from '@/components/ui/cursor-paginator';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useCursorAccumulator } from '@/hooks/useCursorAccumulator';
import type { Resident } from '@hooddesk/shared-types';

interface ResidentListResponse {
  items: Resident[];
  next_cursor?: string | null;
}

interface Block {
  id: string;
  name: string;
}
interface Unit {
  id: string;
  block_id: string | null;
  number: string;
}

const CreateResidentSchema = z.object({
  full_name: z.string().min(2, 'Name required').max(120),
  phone: z.string().regex(/^\+?[1-9]\d{6,14}$/, 'E.164 format (e.g. +9180…)'),
  email: z.union([z.string().email('Invalid email'), z.literal('')]).optional(),
  unit_id: z.string().uuid('Pick a unit'),
  relation: z.enum(['owner', 'tenant', 'family', 'paying_guest']).default('owner'),
  is_primary: z.boolean().default(false),
  lives_in_unit: z.boolean().default(true)
});
type CreateResidentForm = z.infer<typeof CreateResidentSchema>;

interface BulkRow {
  unit_label: string;
  full_name: string;
  phone: string;
  email?: string;
  relation?: 'owner' | 'tenant' | 'family' | 'paying_guest';
  is_primary?: boolean;
}
interface BulkOutcome {
  row: number;
  status: 'created' | 'skipped' | 'error';
  reason?: string;
  resident_id?: string;
}
interface BulkResponse {
  dry_run: boolean;
  total: number;
  created: number;
  skipped: number;
  errors: number;
  rows: BulkOutcome[];
}

function parseCsv(text: string): { rows: BulkRow[]; errors: string[] } {
  const errors: string[] = [];
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length === 0) return { rows: [], errors: ['CSV is empty'] };
  const header = lines[0].split(',').map((s) => s.trim().toLowerCase());
  const required = ['unit_label', 'full_name', 'phone'];
  for (const r of required) {
    if (!header.includes(r)) errors.push(`Missing required column: ${r}`);
  }
  if (errors.length > 0) return { rows: [], errors };
  const idx = (k: string) => header.indexOf(k);
  const rows: BulkRow[] = [];
  for (let i = 1; i < lines.length; i++) {
    const cells = lines[i].split(',').map((s) => s.trim().replace(/^"|"$/g, ''));
    const row: BulkRow = {
      unit_label: cells[idx('unit_label')] ?? '',
      full_name: cells[idx('full_name')] ?? '',
      phone: cells[idx('phone')] ?? ''
    };
    if (idx('email') >= 0) row.email = cells[idx('email')] || undefined;
    if (idx('relation') >= 0) {
      const rel = cells[idx('relation')]?.toLowerCase();
      if (rel && ['owner', 'tenant', 'family', 'paying_guest'].includes(rel)) {
        row.relation = rel as BulkRow['relation'];
      }
    }
    if (idx('is_primary') >= 0) {
      const v = cells[idx('is_primary')]?.toLowerCase();
      row.is_primary = v === 'true' || v === '1' || v === 'yes';
    }
    rows.push(row);
  }
  return { rows, errors };
}

// URL-synced filter+sort+page state. Defaults are dropped from the address
// bar so a fresh /residents URL stays clean. Numbers parse via the hook.
const RESIDENT_FILTER_DEFAULTS = {
  search: '',
  relation: '',
  is_primary: 'all', // 'all' | 'on' | 'off'
  unit_id: '',
  active_status: 'active', // 'active' | 'deleted' | 'all'
  sort: 'created_desc',
  page_size: 50
};

export function ResidentsPage(): JSX.Element {
  const [filterState, setFilterState] = useUrlSearchState(RESIDENT_FILTER_DEFAULTS);
  const [searchInput, setSearchInput] = useState<string>(filterState.search);
  const [open, setOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<Resident | null>(null);
  const [defaultRelation, setDefaultRelation] = useState<'owner' | 'tenant' | 'family' | 'paying_guest'>('owner');
  const [editingId, setEditingId] = useState<string | null>(null);
  // Quick-edit hand-off from the resident profile page (/residents/$id) — the
  // detail page links back here with `?edit=<id>` which we resolve to the
  // existing inline edit dialog. The query-string flag is stripped after open
  // so a refresh / share doesn't keep popping the modal.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const editId = params.get('edit');
    if (editId) {
      setEditingId(editId);
      setOpen(true);
      params.delete('edit');
      const next = params.toString();
      window.history.replaceState(
        null,
        '',
        `${window.location.pathname}${next ? `?${next}` : ''}`
      );
    }
  }, []);
  const [importOpen, setImportOpen] = useState(false);
  const [csvFileName, setCsvFileName] = useState<string | null>(null);
  const [csvRows, setCsvRows] = useState<BulkRow[]>([]);
  const [csvParseErrors, setCsvParseErrors] = useState<string[]>([]);
  const [importPreview, setImportPreview] = useState<BulkResponse | null>(null);
  const queryClient = useQueryClient();

  // Debounce free-text search → URL state to avoid one refetch per keystroke.
  // Other filter changes (relation / sort / page_size) refetch immediately.
  useEffect(() => {
    const t = window.setTimeout(() => {
      if (searchInput !== filterState.search) {
        setFilterState({ search: searchInput });
      }
    }, 300);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // Stable hash of every filter that should reset pagination when it changes.
  // The cursor itself is NOT part of the hash (changing cursor must NOT reset
  // accumulation — that's the whole point of load-more).
  const filtersHash = useMemo(
    () =>
      JSON.stringify([
        filterState.search,
        filterState.relation,
        filterState.is_primary,
        filterState.unit_id,
        filterState.active_status,
        filterState.sort,
        filterState.page_size
      ]),
    [filterState]
  );

  // Build the server query params from the URL state. Empty / default values
  // are stripped before the call so the request stays small.
  const queryParams = useMemo(() => {
    const params: Record<string, string | number | boolean> = {
      limit: filterState.page_size,
      sort: filterState.sort
    };
    if (filterState.search) params.q = filterState.search;
    if (filterState.relation) params.relation = filterState.relation;
    if (filterState.is_primary === 'on') params.is_primary = true;
    if (filterState.is_primary === 'off') params.is_primary = false;
    if (filterState.unit_id) params.unit_id = filterState.unit_id;
    if (filterState.active_status === 'deleted' || filterState.active_status === 'all') {
      params.include_deleted = true;
    }
    return params;
  }, [filterState]);

  // Cursor-paginated accumulator — appends rows on load-more, resets on
  // filtersHash change.
  const [cursor, setCursor] = useState<string | null>(null);
  useEffect(() => {
    setCursor(null);
  }, [filtersHash]);
  const query = useQuery({
    queryKey: ['residents', queryParams, cursor],
    queryFn: () =>
      get<ResidentListResponse>('/residents', {
        params: { ...queryParams, ...(cursor ? { cursor } : {}) }
      }),
    placeholderData: (prev) => prev
  });
  const paginator = useCursorAccumulator<Resident>(query.data, cursor, filtersHash);

  const blocksQuery = useQuery({
    queryKey: ['society', 'blocks'],
    queryFn: () => get<{ items: Block[] }>('/society/blocks')
  });
  const unitsQuery = useQuery({
    queryKey: ['society', 'units', 'all'],
    queryFn: () => get<{ items: Unit[] }>('/society/units', { params: { limit: 200 } })
  });

  // Look up the row from the current list first; fall back to a single-record
  // fetch (with includes=unit) when the resident wasn't on the current page —
  // e.g. when we landed here via "Edit" from /residents/$id profile.
  const listMatch = useMemo(
    () => (editingId ? paginator.items.find((r) => r.id === editingId) ?? null : null),
    [editingId, paginator.items]
  );
  const editingFetch = useQuery({
    queryKey: ['resident', editingId],
    queryFn: () => get<Resident>(`/residents/${editingId}`),
    enabled: Boolean(editingId) && !listMatch
  });
  const editingResident = listMatch ?? editingFetch.data ?? null;

  const blockNameById = useMemo(() => {
    const map = new Map<string, string>();
    (blocksQuery.data?.items ?? []).forEach((b) => map.set(b.id, b.name));
    return map;
  }, [blocksQuery.data]);

  const unitOptions = useMemo(() => {
    const units = unitsQuery.data?.items ?? [];
    return [...units]
      .map((u) => ({
        id: u.id,
        label: `${u.block_id ? blockNameById.get(u.block_id) ?? '?' : '—'} / ${u.number}`,
        sortKey: `${u.block_id ? blockNameById.get(u.block_id) ?? 'zzz' : 'zzz'}-${u.number}`
      }))
      .sort((a, b) => a.sortKey.localeCompare(b.sortKey, undefined, { numeric: true }));
  }, [unitsQuery.data, blockNameById]);

  const form = useForm<CreateResidentForm>({
    resolver: zodResolver(CreateResidentSchema),
    defaultValues: {
      full_name: '',
      phone: '',
      email: '',
      unit_id: '',
      relation: 'owner',
      is_primary: false,
      lives_in_unit: true
    }
  });

  // When the Add dialog is opened with a pre-set relation (e.g. tenant via the
  // "Add tenant" quick-button), keep the form in sync so the relation Select
  // reflects the chosen default the moment the modal renders.
  useEffect(() => {
    if (open && !editingId) {
      form.setValue('relation', defaultRelation);
    }
  }, [open, editingId, defaultRelation, form]);

  const create = useMutation({
    mutationFn: (values: CreateResidentForm) =>
      post<Resident, Omit<CreateResidentForm, 'email'> & { email?: string }>('/residents', {
        ...values,
        email: values.email?.trim() ? values.email : undefined
      }),
    onSuccess: (r) => {
      toast.success(`${r.full_name} added`);
      void queryClient.invalidateQueries({ queryKey: ['residents'] });
      setOpen(false);
      setDefaultRelation('owner');
      form.reset();
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const remove = useMutation({
    mutationFn: (id: string) => del<void>(`/residents/${id}`),
    onSuccess: () => {
      toast.success('Resident removed');
      void queryClient.invalidateQueries({ queryKey: ['residents'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const update = useMutation({
    mutationFn: ({ id, values }: { id: string; values: CreateResidentForm }) =>
      patch<Resident>(`/residents/${id}`, {
        ...values,
        email: values.email?.trim() ? values.email : undefined
      }),
    onSuccess: (r) => {
      toast.success(`${r.full_name} updated`);
      void queryClient.invalidateQueries({ queryKey: ['residents'] });
      setEditingId(null);
      setOpen(false);
      form.reset();
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  // Prefill form when entering edit mode
  useEffect(() => {
    if (editingResident && open) {
      form.reset({
        full_name: editingResident.full_name,
        phone: editingResident.phone,
        email: editingResident.email ?? '',
        unit_id: editingResident.unit_id,
        relation: editingResident.relation,
        is_primary: editingResident.is_primary,
        lives_in_unit: editingResident.lives_in_unit !== false
      });
    } else if (!open && !editingId) {
      form.reset({
        full_name: '',
        phone: '',
        email: '',
        unit_id: '',
        relation: defaultRelation,
        is_primary: false,
        lives_in_unit: true
      });
    }
  }, [editingResident, open, editingId, defaultRelation, form]);

  const onSubmit = (v: CreateResidentForm) => {
    if (editingId) {
      update.mutate({ id: editingId, values: v });
    } else {
      create.mutate(v);
    }
  };

  const closeDialog = () => {
    setOpen(false);
    setEditingId(null);
    setDefaultRelation('owner');
  };

  const bulkImport = useMutation({
    mutationFn: (params: { rows: BulkRow[]; dry_run: boolean }) =>
      post<BulkResponse, { rows: BulkRow[]; dry_run: boolean }>('/residents/bulk-import', params),
    onSuccess: (res) => {
      setImportPreview(res);
      if (!res.dry_run && res.created > 0) {
        toast.success(`Imported ${res.created} resident${res.created === 1 ? '' : 's'}`);
        void queryClient.invalidateQueries({ queryKey: ['residents'] });
      } else if (!res.dry_run) {
        toast.warning(`No residents created (${res.skipped} skipped, ${res.errors} errors)`);
      }
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const onCsvFile = (file: File) => {
    setCsvFileName(file.name);
    setImportPreview(null);
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result ?? '');
      const { rows, errors } = parseCsv(text);
      setCsvRows(rows);
      setCsvParseErrors(errors);
    };
    reader.onerror = () => {
      setCsvParseErrors(['Could not read file']);
      setCsvRows([]);
    };
    reader.readAsText(file);
  };

  const closeImport = () => {
    setImportOpen(false);
    setCsvFileName(null);
    setCsvRows([]);
    setCsvParseErrors([]);
    setImportPreview(null);
  };

  const data = useMemo<Resident[]>(() => paginator.items, [paginator.items]);

  const importDialog = (
    <Dialog open={importOpen} onOpenChange={(v) => (v ? setImportOpen(true) : closeImport())}>
      <DialogTrigger asChild>
        <Button variant="secondary" size="sm">
          <Upload className="h-4 w-4" /> Bulk import CSV
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Bulk import residents</DialogTitle>
          <DialogDescription>
            CSV with header row: <code className="rounded bg-vt-surface-2 px-1 py-0.5 font-mono text-[11px]">unit_label,full_name,phone,email,relation,is_primary</code>.
            Required: unit_label, full_name, phone. Other columns optional. Existing phones are skipped.
          </DialogDescription>
        </DialogHeader>

        {!csvFileName ? (
          <label className="flex cursor-pointer flex-col items-center justify-center gap-vt-2 rounded-vt-md border-2 border-dashed border-vt-border bg-vt-surface-2 px-vt-6 py-vt-8 text-center hover:border-vt-primary-400">
            <Upload className="h-8 w-8 text-vt-text-soft" strokeWidth={1.5} />
            <span className="text-vt-13 font-medium text-vt-text">Drop a CSV file or click to browse</span>
            <span className="text-vt-12 text-vt-text-soft">UTF-8, comma-separated, header row required</span>
            <input
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onCsvFile(f);
              }}
            />
          </label>
        ) : (
          <div className="flex flex-col gap-vt-3">
            <div className="flex items-center justify-between rounded-vt-md border border-vt-border bg-vt-surface-2 px-vt-3 py-vt-2 text-vt-13">
              <span className="truncate font-mono text-vt-text-muted">{csvFileName}</span>
              <button
                type="button"
                className="text-vt-12 text-vt-text-soft underline-offset-2 hover:underline"
                onClick={() => {
                  setCsvFileName(null);
                  setCsvRows([]);
                  setCsvParseErrors([]);
                  setImportPreview(null);
                }}
              >
                Replace
              </button>
            </div>

            {csvParseErrors.length > 0 ? (
              <div className="rounded-vt-md border border-vt-alert/30 bg-[color:var(--vt-alert-100)] px-vt-3 py-vt-2 text-vt-13 text-vt-alert">
                {csvParseErrors.map((e, i) => (
                  <div key={i}>· {e}</div>
                ))}
              </div>
            ) : (
              <div className="text-vt-13 text-vt-text-muted">
                Parsed <strong>{csvRows.length}</strong> data row{csvRows.length === 1 ? '' : 's'} from CSV.
              </div>
            )}

            {importPreview ? (
              <div className="rounded-vt-md border border-vt-border bg-vt-surface-2 px-vt-3 py-vt-3">
                <div className="mb-vt-2 flex flex-wrap items-center gap-vt-3 text-vt-13">
                  <Badge variant="ok">Created {importPreview.created}</Badge>
                  <Badge variant="warn">Skipped {importPreview.skipped}</Badge>
                  <Badge variant="alert">Errors {importPreview.errors}</Badge>
                  {importPreview.dry_run ? <Badge variant="muted">Dry-run preview</Badge> : <Badge variant="info">Committed</Badge>}
                </div>
                <div className="max-h-48 overflow-auto rounded-vt-sm border border-vt-border bg-vt-surface text-vt-12">
                  <table className="w-full">
                    <thead className="sticky top-0 bg-vt-surface-2 text-vt-text-muted">
                      <tr>
                        <th className="px-2 py-1 text-left">Row</th>
                        <th className="px-2 py-1 text-left">Status</th>
                        <th className="px-2 py-1 text-left">Detail</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importPreview.rows.map((r) => (
                        <tr key={r.row} className="border-t border-vt-border">
                          <td className="px-2 py-1 font-mono">{r.row}</td>
                          <td className="px-2 py-1">{r.status}</td>
                          <td className="px-2 py-1 text-vt-text-muted">
                            {r.reason ?? r.resident_id ?? ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={closeImport}>
            {importPreview && !importPreview.dry_run ? 'Close' : 'Cancel'}
          </Button>
          {csvRows.length > 0 && csvParseErrors.length === 0 && !importPreview ? (
            <Button
              variant="secondary"
              onClick={() => bulkImport.mutate({ rows: csvRows, dry_run: true })}
              loading={bulkImport.isPending}
              loadingText="Previewing…"
            >
              Preview
            </Button>
          ) : null}
          {csvRows.length > 0 && csvParseErrors.length === 0 && importPreview?.dry_run ? (
            <Button
              onClick={() => bulkImport.mutate({ rows: csvRows, dry_run: false })}
              loading={bulkImport.isPending}
              loadingText="Importing…"
            >
              Commit {csvRows.length - importPreview.errors - importPreview.skipped} new
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  // "Add tenant" quick-button opens the same dialog with relation pre-set to
  // tenant. Used both inline (toolbar) and as the empty-state action when the
  // active relation filter is `tenant`.
  const openAddTenant = () => {
    setEditingId(null);
    setDefaultRelation('tenant');
    setOpen(true);
  };

  const addTenantButton = (
    <Button
      size="sm"
      variant="secondary"
      onClick={openAddTenant}
      title="Add a tenant (opens the same dialog with relation pre-set)"
    >
      <UserPlus className="h-4 w-4" /> Add tenant
    </Button>
  );

  const formDialog = (
    <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : closeDialog())}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          onClick={() => {
            setEditingId(null);
            setDefaultRelation('owner');
          }}
        >
          <Plus className="h-4 w-4" /> Add resident
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editingId ? 'Edit resident' : 'Add resident'}</DialogTitle>
          <DialogDescription>
            {editingId
              ? 'Update name, unit, contact details, or relation. Phone change re-issues OTP login.'
              : 'They will receive an OTP login on the phone provided. Email is optional.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-vt-4">
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="full_name">Full name</Label>
            <Input
              id="full_name"
              placeholder="Priya Sharma"
              autoFocus
              aria-invalid={Boolean(form.formState.errors.full_name) || undefined}
              {...form.register('full_name')}
            />
            {form.formState.errors.full_name ? (
              <p className="text-vt-12 text-vt-alert" role="alert">{form.formState.errors.full_name.message}</p>
            ) : null}
          </div>
          <div className="grid grid-cols-2 gap-vt-3">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                placeholder="+919812345678"
                aria-invalid={Boolean(form.formState.errors.phone) || undefined}
                {...form.register('phone')}
              />
              {form.formState.errors.phone ? (
                <p className="text-vt-12 text-vt-alert" role="alert">{form.formState.errors.phone.message}</p>
              ) : null}
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="email">Email (optional)</Label>
              <Input
                id="email"
                type="email"
                placeholder="priya@example.com"
                aria-invalid={Boolean(form.formState.errors.email) || undefined}
                {...form.register('email')}
              />
              {form.formState.errors.email ? (
                <p className="text-vt-12 text-vt-alert" role="alert">{form.formState.errors.email.message}</p>
              ) : null}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-vt-3">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="unit_id">Unit</Label>
              <Select
                id="unit_id"
                aria-invalid={Boolean(form.formState.errors.unit_id) || undefined}
                disabled={unitsQuery.isLoading}
                {...form.register('unit_id')}
              >
                <option value="">{unitsQuery.isLoading ? 'Loading units…' : 'Select unit'}</option>
                {unitOptions.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.label}
                  </option>
                ))}
              </Select>
              {form.formState.errors.unit_id ? (
                <p className="text-vt-12 text-vt-alert" role="alert">{form.formState.errors.unit_id.message}</p>
              ) : null}
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="relation">Relation</Label>
              <Select id="relation" {...form.register('relation')}>
                <option value="owner">Owner</option>
                <option value="tenant">Tenant</option>
                <option value="family">Family</option>
                <option value="paying_guest">Paying guest</option>
              </Select>
            </div>
          </div>
          <label className="flex items-center gap-vt-2 text-vt-13 text-vt-text">
            <input type="checkbox" {...form.register('is_primary')} className="h-4 w-4" />
            Primary contact for this unit
          </label>
          <label className="flex items-start gap-vt-2 text-vt-13 text-vt-text">
            <input
              type="checkbox"
              {...form.register('lives_in_unit')}
              className="mt-0.5 h-4 w-4"
            />
            <span className="flex-1">
              Lives in this unit
              <span className="block text-vt-12 text-vt-text-muted">
                Uncheck for owners who own this flat but live elsewhere (rented out,
                investor owner, etc.). Society retains their contact info but they
                show as "Offsite".
              </span>
            </span>
          </label>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={closeDialog}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={create.isPending || update.isPending}
              loadingText={editingId ? 'Saving…' : 'Adding…'}
            >
              {editingId ? 'Save changes' : 'Add resident'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );

  const columns = useMemo<ColumnDef<Resident>[]>(
    () => [
      {
        accessorKey: 'full_name',
        header: 'Resident',
        cell: ({ row }) => (
          <Link
            to="/residents/$id"
            params={{ id: row.original.id }}
            className="flex items-center gap-vt-3 rounded-vt-sm outline-none ring-vt-primary-400/60 hover:bg-vt-surface-2/50 focus-visible:ring-2"
            aria-label={`Open ${row.original.full_name}'s profile`}
          >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-vt-primary-100 text-vt-12 font-semibold text-vt-primary">
              {row.original.full_name.split(' ').map((s) => s[0]).join('').slice(0, 2).toUpperCase()}
            </span>
            <div className="min-w-0">
              <div className="truncate font-medium text-vt-text hover:underline">
                {row.original.full_name}
              </div>
              <div className="truncate text-vt-12 text-vt-text-muted">{row.original.phone}</div>
            </div>
          </Link>
        )
      },
      {
        accessorKey: 'relation',
        header: 'Relation',
        cell: ({ getValue }) => (
          <Badge variant="muted" className="capitalize">
            {String(getValue() ?? '—').replace('_', ' ')}
          </Badge>
        )
      },
      {
        accessorKey: 'email',
        header: 'Email',
        cell: ({ getValue }) => (
          <span className="text-vt-text-muted">{(getValue() as string) ?? '—'}</span>
        )
      },
      {
        accessorKey: 'unit_id',
        header: 'Unit',
        cell: ({ row }) => {
          const u = (row.original as unknown as { unit?: { number?: string; block?: { name?: string } } }).unit;
          if (!u?.number) {
            return <span className="text-vt-text-muted">—</span>;
          }
          return (
            <span className="text-vt-text">
              {u.block?.name ? <span className="text-vt-text-muted">{u.block.name} / </span> : null}
              {u.number}
            </span>
          );
        }
      },
      {
        accessorKey: 'is_primary',
        header: 'Primary',
        cell: ({ getValue }) => (getValue() ? <Badge variant="ok">Primary</Badge> : null)
      },
      {
        accessorKey: 'lives_in_unit',
        header: 'Resides',
        // false (or absent boolean) → 'Offsite' badge so admins immediately
        // see owners who've rented out the flat but stay on file.
        cell: ({ row }) =>
          row.original.lives_in_unit === false ? (
            <Badge variant="warn">Offsite</Badge>
          ) : (
            <Badge variant="muted">Onsite</Badge>
          )
      },
      {
        id: 'actions',
        header: () => <span className="sr-only">Actions</span>,
        cell: ({ row }) => (
          <div className="flex justify-end gap-vt-1">
            <Link
              to="/residents/$id/ledger"
              params={{ id: row.original.id }}
              aria-label="View ledger"
              title="Ledger"
              className="inline-flex h-7 w-7 items-center justify-center rounded-vt-sm text-vt-text-muted hover:bg-vt-surface-2 hover:text-vt-text"
            >
              <Receipt className="h-4 w-4" />
            </Link>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Edit resident"
              title="Edit"
              className="h-7 w-7 text-vt-text-muted"
              onClick={() => {
                setEditingId(row.original.id);
                setOpen(true);
              }}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Remove resident"
              title="Remove"
              className="h-7 w-7 text-vt-text-muted"
              disabled={remove.isPending}
              onClick={() => setRemoveTarget(row.original)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        )
      }
    ],
    [remove]
  );

  const table = useReactTable({ data, columns, getCoreRowModel: getCoreRowModel() });

  // Sort + filter chip wiring for the shared toolbar.
  const sortOptions = useMemo(
    () => [
      { value: 'created_desc', label: 'Created (newest)' },
      { value: 'created_asc', label: 'Created (oldest)' },
      { value: 'name_asc', label: 'Name (A–Z)' },
      { value: 'name_desc', label: 'Name (Z–A)' }
    ],
    []
  );

  const filterChips: FilterChip[] = useMemo(
    () => [
      {
        id: 'relation',
        label: 'Relation',
        type: 'select',
        value: filterState.relation,
        options: [
          { value: '', label: 'All' },
          { value: 'owner', label: 'Owner' },
          { value: 'tenant', label: 'Tenant' },
          { value: 'family', label: 'Family' },
          { value: 'paying_guest', label: 'Paying guest' }
        ],
        onChange: (v) => setFilterState({ relation: v })
      },
      {
        id: 'is_primary',
        label: 'Primary contact',
        type: 'toggle',
        value: filterState.is_primary as 'all' | 'on' | 'off',
        onLabel: 'Primary only',
        offLabel: 'Non-primary',
        onChange: (v) => setFilterState({ is_primary: v })
      },
      {
        id: 'unit_id',
        label: 'Unit',
        type: 'select',
        value: filterState.unit_id,
        options: [
          { value: '', label: 'All units' },
          ...unitOptions.map((u) => ({ value: u.id, label: u.label }))
        ],
        onChange: (v) => setFilterState({ unit_id: v })
      },
      {
        id: 'active_status',
        label: 'Status',
        type: 'select',
        value: filterState.active_status,
        options: [
          { value: 'active', label: 'Active only' },
          { value: 'deleted', label: 'Deleted only' },
          { value: 'all', label: 'All (incl. deleted)' }
        ],
        onChange: (v) => setFilterState({ active_status: v })
      }
    ],
    [filterState, unitOptions, setFilterState]
  );

  const hasActiveFilters =
    filterState.search !== RESIDENT_FILTER_DEFAULTS.search ||
    filterState.relation !== RESIDENT_FILTER_DEFAULTS.relation ||
    filterState.is_primary !== RESIDENT_FILTER_DEFAULTS.is_primary ||
    filterState.unit_id !== RESIDENT_FILTER_DEFAULTS.unit_id ||
    filterState.active_status !== RESIDENT_FILTER_DEFAULTS.active_status ||
    filterState.sort !== RESIDENT_FILTER_DEFAULTS.sort ||
    filterState.page_size !== RESIDENT_FILTER_DEFAULTS.page_size;

  const resetFilters = () => {
    setFilterState(RESIDENT_FILTER_DEFAULTS);
    setSearchInput('');
  };

  const isTenantView = filterState.relation === 'tenant';

  return (
    <>
      <PageHeader
        title="Residents"
        description="Owners, tenants and family members on file. Search, import, or onboard new residents."
        actions={
          <>
            {importDialog}
            {addTenantButton}
            {formDialog}
          </>
        }
      />
      <div className="mx-auto w-full max-w-[1400px] px-vt-6 pb-vt-8 pt-vt-4 space-y-vt-4">
        <AdvancedListToolbar
          searchValue={searchInput}
          onSearchChange={setSearchInput}
          searchPlaceholder="Search by name, phone, or email…"
          filters={filterChips}
          sort={{
            value: filterState.sort,
            onChange: (v) => setFilterState({ sort: v }),
            options: sortOptions
          }}
          pageSize={filterState.page_size}
          onPageSizeChange={(n) => setFilterState({ page_size: n })}
          onReset={resetFilters}
          hasActiveFilters={hasActiveFilters}
          resultLabel={
            query.data
              ? `${data.length} result${data.length === 1 ? '' : 's'}`
              : 'Loading…'
          }
        />

        <Card>
          <CardContent className="space-y-vt-4 p-vt-4">
            {query.isLoading ? (
              <div className="flex flex-col gap-vt-2">
                {Array.from({ length: 8 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : query.isError ? (
              <EmptyState
                icon={<Users className="h-6 w-6" strokeWidth={1.75} />}
                title="Unable to load residents"
                description={(query.error as Error)?.message ?? 'Check your permissions and try again.'}
                action={
                  <Button variant="secondary" onClick={() => void query.refetch()}>
                    Try again
                  </Button>
                }
              />
            ) : data.length === 0 ? (
              hasActiveFilters ? (
                <EmptyState
                  icon={<Users className="h-6 w-6" strokeWidth={1.75} />}
                  title="No residents match your filters"
                  description="Try widening the filters or clear them to see everyone on file."
                  action={
                    <Button variant="secondary" onClick={resetFilters}>
                      Reset filters
                    </Button>
                  }
                />
              ) : (
                <EmptyState
                  icon={<Users className="h-6 w-6" strokeWidth={1.75} />}
                  title={isTenantView ? 'No tenants yet' : 'No residents yet'}
                  description={
                    isTenantView
                      ? 'Add one tenant at a time using the button above, or use Bulk import CSV.'
                      : 'Add one resident at a time using the button above, or use Bulk import CSV for a fresh society.'
                  }
                  action={
                    isTenantView ? (
                      <Button onClick={openAddTenant}>
                        <UserPlus className="h-4 w-4" /> Add tenant
                      </Button>
                    ) : (
                      <Button
                        onClick={() => {
                          setEditingId(null);
                          setDefaultRelation('owner');
                          setOpen(true);
                        }}
                      >
                        <Plus className="h-4 w-4" /> Add first resident
                      </Button>
                    )
                  }
                />
              )
            ) : (
              <>
                <Table>
                  <TableHeader>
                    {table.getHeaderGroups().map((hg) => (
                      <TableRow key={hg.id}>
                        {hg.headers.map((h) => (
                          <TableHead key={h.id}>
                            {h.isPlaceholder ? null : flexRender(h.column.columnDef.header, h.getContext())}
                          </TableHead>
                        ))}
                      </TableRow>
                    ))}
                  </TableHeader>
                  <TableBody>
                    {table.getRowModel().rows.map((row) => (
                      <TableRow key={row.id}>
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <CursorPaginator
                  hasMore={Boolean(paginator.nextCursor)}
                  loading={query.isFetching}
                  itemsShown={data.length}
                  pageSize={filterState.page_size}
                  onLoadMore={() => {
                    if (paginator.nextCursor) setCursor(paginator.nextCursor);
                  }}
                />
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <ConfirmDialog
        open={Boolean(removeTarget)}
        onOpenChange={(v) => (v ? null : setRemoveTarget(null))}
        title="Remove resident"
        description={
          removeTarget ? (
            <>
              Remove <strong>{removeTarget.full_name}</strong> from residents? They lose admin
              access and their unit assignment is cleared.
            </>
          ) : null
        }
        confirmLabel="Remove resident"
        variant="danger"
        loading={remove.isPending}
        onConfirm={() => {
          if (!removeTarget) return;
          remove.mutate(removeTarget.id, { onSuccess: () => setRemoveTarget(null) });
        }}
      />
    </>
  );
}
